import { useState } from 'react';
import { Alert, Collapse, Space, Spin, Tag, Typography } from 'antd';
import type { HermesDialog, HermesHistoryEntry } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { getHermesEstimateDialog } from './api.ts';

// Кто из суб-агентов Гермеса спрашивал — словами, а не именем агента.
const AGENT_LABEL: Record<string, string> = {
  clarify_scope: 'Состав работ',
  fill_checklist: 'Опросник',
};

interface HermesDialogBlockProps {
  spaceId: string;
  itemId: string;
  entry: HermesHistoryEntry;
}

/**
 * Номер задачи в Гермесе и разговор оценки: какие вопросы задавал Гермес и
 * что ответил человек. Разговор грузим при первом раскрытии блока.
 */
export function HermesDialogBlock({
  spaceId,
  itemId,
  entry,
}: HermesDialogBlockProps): JSX.Element | null {
  const [dialog, setDialog] = useState<HermesDialog | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!entry.taskId) return null;

  const load = async (): Promise<void> => {
    if (dialog || loading) return;
    setLoading(true);
    setError(null);
    try {
      setDialog(await getHermesEstimateDialog(spaceId, itemId, entry.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить разговор из Гермеса');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        Задача в Гермесе:{' '}
        <Typography.Text
          code
          copyable={{ text: entry.taskId, tooltips: ['Скопировать', 'Скопировано'] }}
        >
          {entry.taskId}
        </Typography.Text>
      </Typography.Text>
      <Collapse
        ghost
        size="small"
        onChange={(keys) => {
          if ((Array.isArray(keys) ? keys : [keys]).includes('dialog')) void load();
        }}
        items={[
          {
            key: 'dialog',
            label: <Typography.Text type="secondary">Разговор с Гермесом</Typography.Text>,
            children: (
              <DialogBody
                dialog={dialog}
                loading={loading}
                error={error}
                onRetry={() => void load()}
              />
            ),
          },
        ]}
      />
    </Space>
  );
}

function DialogBody({
  dialog,
  loading,
  error,
  onRetry,
}: {
  dialog: HermesDialog | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}): JSX.Element | null {
  if (loading) return <Spin size="small" />;
  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message={error}
        action={
          <Typography.Link onClick={onRetry} style={{ whiteSpace: 'nowrap' }}>
            Повторить
          </Typography.Link>
        }
      />
    );
  }
  if (!dialog) return null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {dialog.taskDescription && (
        <div>
          <Typography.Text strong>Описание задачи</Typography.Text>
          <Typography.Paragraph
            style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}
            ellipsis={{
              rows: 3,
              expandable: 'collapsible',
              symbol: (expanded) => (expanded ? 'свернуть' : 'полностью'),
            }}
          >
            {dialog.taskDescription}
          </Typography.Paragraph>
        </div>
      )}
      {dialog.rounds.length === 0 ? (
        <Typography.Text type="secondary">
          Гермес ничего не спрашивал — оценил по описанию.
        </Typography.Text>
      ) : (
        dialog.rounds.map((r, i) => (
          <div
            key={i}
            style={{ borderLeft: '2px solid var(--ant-color-border, #d9d9d9)', paddingLeft: 12 }}
          >
            <Space size={6} style={{ marginBottom: 4 }}>
              <Tag>{r.agentName ? (AGENT_LABEL[r.agentName] ?? r.agentName) : 'Вопрос'}</Tag>
              {r.askedAt && (
                <Typography.Text type="secondary">
                  {new Date(r.askedAt).toLocaleString('ru-RU')}
                </Typography.Text>
              )}
            </Space>
            {r.question && (
              <Typography.Paragraph
                type="secondary"
                style={{ whiteSpace: 'pre-wrap', marginBottom: 6 }}
                ellipsis={{
                  rows: 4,
                  expandable: 'collapsible',
                  symbol: (expanded) => (expanded ? 'свернуть' : 'полностью'),
                }}
              >
                {r.question}
              </Typography.Paragraph>
            )}
            {r.answers.length === 0 ? (
              <Typography.Text type="secondary" italic>
                Ответа нет
              </Typography.Text>
            ) : (
              r.answers.map((a, j) => (
                <Typography.Paragraph key={j} style={{ marginBottom: 4 }}>
                  <Typography.Text type="secondary">{a.question}</Typography.Text>
                  <br />
                  <span style={{ whiteSpace: 'pre-wrap' }}>{a.answer}</span>
                </Typography.Paragraph>
              ))
            )}
          </div>
        ))
      )}
    </Space>
  );
}
